/**
 * Resizable Modal Component
 * Modal ridimensionabile e trascinabile per l'interfaccia AI
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Maximize2, Minimize2, RotateCcw, Save } from 'lucide-react';

export interface TabItem {
  id: string;
  label: string;
  shortLabel?: string;  // Label abbreviata per mobile
  icon?: React.ComponentType<{ size?: number; className?: string }>;
  children?: TabItem[];  // Sottomenu per dropdown
}

export interface ScrollPhysics {
  friction?: number;      // Decelerazione (0-1), default: 0.95
  elasticity?: number;    // Rimbalzo ai bordi (0-1), default: 0.5
  minVelocity?: number;   // Velocità minima per fermarsi, default: 0.5
}

export interface VisualEffects {
  enableGlow?: boolean;           // Glow sui tab attivi, default: true
  enableGradients?: boolean;      // Gradienti sui bottoni, default: true
  enableShadows?: boolean;        // Ombre dinamiche, default: true
  enableShine?: boolean;          // Effetto lucido sui tab, default: true
  enablePulse?: boolean;          // Pulsazione su hover, default: false
  glowColor?: string;             // Colore glow, default: 'blue'
  accentColor?: string;           // Colore accento, default: 'blue'
}

interface ResizableModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string; // Opzionale se hideHeader è true
  titleIcon?: string; // URL dell'icona da mostrare accanto al titolo
  subtitle?: React.ReactNode; // Sottotitolo o badge (supporta ReactNode)
  headerCenter?: React.ReactNode; // Contenuto centrale dell'header
  headerActions?: React.ReactNode; // Contenuto a destra dell'header (prima dei controlli)
  initialWidth?: number;
  initialHeight?: number;
  initialX?: number;
  initialY?: number;
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
  enableResize?: boolean;
  enableDrag?: boolean;
  forceCenter?: boolean;
  followWindowResize?: boolean;
  persistenceKey?: string;
  onSizeChange?: (width: number, height: number) => void;
  footer?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  // Props per footer responsivo
  onSave?: () => void;
  saveLabel?: string;
  context?: any;
  // Props per tabs parametrizzabili
  tabs?: TabItem[];
  activeTab?: string;
  onTabChange?: (tabId: string) => void;
  // Props per personalizzazione physics e visual
  scrollPhysics?: ScrollPhysics;
  visualEffects?: VisualEffects;
  // Z-index per stacking modali
  zIndex?: number;
  // Permette al contenuto di overflow (utile per dropdown)
  allowContentOverflow?: boolean;

  /**
   * === PROPS PER MODALITÀ MULTI-FINESTRA ===
   *
   * Queste props permettono di usare più ResizableModal contemporaneamente
   * con gestione focus indipendente e backdrop condiviso.
   *
   * Pattern d'uso tipico:
   * 1. Creare un backdrop condiviso esterno con z-index basso (es. 90)
   * 2. Usare queste props per disabilitare i backdrop individuali
   * 3. Gestire z-index dinamici con onModalFocus per lo swap focus
   *
   * Esempio:
   * ```tsx
   * const [windowOnTop, setWindowOnTop] = useState<'main' | 'preview'>('main');
   *
   * <ResizableModal
   *   zIndex={windowOnTop === 'main' ? 200 : 100}
   *   disableBackdropBlur={hasSecondWindow}
   *   disableBackdropClick={hasSecondWindow}
   *   disableBackdropDarkening={hasSecondWindow}
   *   onModalFocus={() => setWindowOnTop('main')}
   * />
   * ```
   *
   * Vedi TestMockupPreventivoPage.tsx per implementazione completa.
   */

  /**
   * Disabilita il backdrop blur della modale.
   * Usare quando c'è un backdrop condiviso esterno che già applica il blur.
   * @default false
   */
  disableBackdropBlur?: boolean;

  /**
   * Intensità della sfocatura del backdrop in pixel.
   * Valori comuni: 4 (sm), 8 (md), 12 (lg), 16 (xl).
   * @default 3
   */
  backdropBlurAmount?: number;

  /**
   * Disabilita il click sul backdrop per chiudere la modale.
   * Usare in modalità multi-finestra per evitare che click su altre finestre
   * chiudano questa modale. Il backdrop avrà pointer-events: none.
   * @default false
   */
  disableBackdropClick?: boolean;

  /**
   * Disabilita l'oscuramento (bg-black/50) del backdrop.
   * Usare in modalità multi-finestra per evitare che una finestra
   * con focus oscuri le altre finestre con z-index inferiore.
   * @default false
   */
  disableBackdropDarkening?: boolean;

  /**
   * Percentuale di oscuramento del backdrop (0-100).
   * 0 = trasparente, 50 = semitrasparente, 100 = nero pieno.
   * @default 50
   */
  backdropDarkeningAmount?: number;

  /**
   * Callback chiamato quando l'utente clicca sulla finestra modale.
   * Usare per gestire lo swap di z-index tra finestre multiple.
   * Viene triggerato su onMouseDown del container modale.
   *
   * @example
   * onModalFocus={() => setWindowOnTop('main')}
   */
  onModalFocus?: () => void;

  /**
   * Usa posizionamento assoluto invece di fisso per il container esterno.
   * Utile quando si vuole permettere lo scroll della pagina per vedere
   * modali posizionate fuori dalla viewport (es. layout verticale mobile).
   *
   * Quando true:
   * - Il container usa `position: absolute` invece di `fixed`
   * - Richiede un parent con `position: relative` e `overflow: auto`
   * - La pagina diventa scrollabile per vedere il contenuto
   *
   * @default false
   */
  useAbsolutePositioning?: boolean;

  /**
   * Nasconde l'header standard della modale.
   * Usare quando si vuole un header completamente personalizzato
   * all'interno del children.
   * @default false
   */
  hideHeader?: boolean;
}

const ResizableModal: React.FC<ResizableModalProps> = ({
  isOpen,
  onClose,
  title,
  titleIcon,
  subtitle,
  headerCenter,
  headerActions,
  initialWidth = 800,
  initialHeight = 600,
  initialX,
  initialY,
  minWidth = 400,
  minHeight = 300,
  maxWidth,
  maxHeight,
  enableResize = true,
  enableDrag = true,
  forceCenter = false,
  followWindowResize = false,
  persistenceKey: _persistenceKey,
  onSizeChange,
  footer,
  children,
  className = '',
  // Props per footer responsivo
  onSave,
  saveLabel: _saveLabel = 'Salva',
  context,
  // Props per tabs
  tabs,
  activeTab,
  onTabChange,
  // Props per personalizzazione
  scrollPhysics = {},
  visualEffects = {},
  zIndex = 50,
  allowContentOverflow = false,
  disableBackdropBlur = false,
  backdropBlurAmount = 3,
  disableBackdropClick = false,
  disableBackdropDarkening = false,
  backdropDarkeningAmount = 50,
  onModalFocus,
  useAbsolutePositioning = false,
  hideHeader = false
}) => {
  // Valori di default per physics
  const physics = {
    friction: scrollPhysics.friction ?? 0.95,
    elasticity: scrollPhysics.elasticity ?? 0.5,
    minVelocity: scrollPhysics.minVelocity ?? 0.5
  };

  // Valori di default per visual effects
  const effects = {
    enableGlow: visualEffects.enableGlow ?? true,
    enableGradients: visualEffects.enableGradients ?? true,
    enableShadows: visualEffects.enableShadows ?? true,
    enableShine: visualEffects.enableShine ?? true,
    enablePulse: visualEffects.enablePulse ?? false,
    glowColor: visualEffects.glowColor ?? 'blue',
    accentColor: visualEffects.accentColor ?? 'blue'
  };
  const [size, setSize] = useState({ width: initialWidth, height: initialHeight });
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isMaximized, setIsMaximized] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [resizeDirection, setResizeDirection] = useState<string>('');
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [savedSize, setSavedSize] = useState({ width: initialWidth, height: initialHeight });
  const [savedPosition, setSavedPosition] = useState({ x: 0, y: 0 });
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });

  // Stati per il sistema proporzionale
  const [widthRatio, setWidthRatio] = useState<number | null>(null);
  const [marginRatio, setMarginRatio] = useState<number | null>(null);

  const modalRef = useRef<HTMLDivElement>(null);
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const activeTabRef = useRef<HTMLButtonElement>(null);

  // Stati per drag delle tabs
  const [isTabDragging, setIsTabDragging] = useState(false);
  const [tabDragStart, setTabDragStart] = useState({ x: 0, scrollLeft: 0, time: 0 });
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const isMouseDownRef = useRef(false);
  const wasDraggingRef = useRef(false);
  const velocityRef = useRef(0);
  const lastPositionRef = useRef({ x: 0, time: 0 });
  const inertiaAnimationRef = useRef<number | null>(null);

  // Stati per le frecce di scroll
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  // Stato per forzare re-render dropdown durante scroll
  const [, setScrollTick] = useState(0);

  // Responsive title sizing based on modal width
  const getTitleResponsiveClass = () => {
    const width = size.width;

    if (width < 500) {
      return 'text-sm font-medium truncate'; // Smaller and truncated for very small
    } else if (width < 700) {
      return 'text-base font-semibold truncate'; // Medium size, still truncated
    } else {
      return 'text-lg font-semibold'; // Full size for larger modals
    }
  };

  const getSubtitleResponsiveClass = () => {
    const width = size.width;

    if (width < 500) {
      return 'text-xs mt-0.5 truncate'; // Minimal for very small
    } else if (width < 700) {
      return 'text-xs mt-1 truncate'; // Small, truncated
    } else {
      return 'text-sm mt-1'; // Normal size
    }
  };

  // Calcola i limiti massimi dinamicamente con supporto mobile
  const isMobileScreen = windowSize.width < 768;
  const dynamicMaxWidth = maxWidth || windowSize.width - 40; // Margine decente per tutti i dispositivi
  const dynamicMaxHeight = maxHeight || (isMobileScreen ? windowSize.height - 50 : windowSize.height - 80);

  // Calcola dimensioni sicure per il dispositivo corrente
  const getSafeInitialWidth = (requestedWidth: number) => {
    const maxSafeWidth = windowSize.width - 40; // 20px margine per lato
    const isMobile = windowSize.width < 768; // Breakpoint mobile standard

    if (isMobile) {
      // Su mobile: usa quasi tutto lo schermo ma lascia margini
      return Math.min(requestedWidth, maxSafeWidth, windowSize.width * 0.95);
    }

    // Su desktop: comportamento normale
    return Math.min(requestedWidth, maxSafeWidth);
  };

  const getSafeInitialHeight = (requestedHeight: number) => {
    const isMobile = windowSize.width < 768;

    if (isMobile) {
      // Su mobile: margini più sicuri per UI browser/sistema (10px top + 40px bottom per barre)
      const mobileMaxHeight = windowSize.height - 50; // Margine totale 50px (10px top + 40px bottom)
      console.log('📱 Mobile modal: using safe screen height', {
        screenHeight: windowSize.height,
        modalHeight: mobileMaxHeight,
        margin: '50px total (10px top + 40px bottom)'
      });
      return mobileMaxHeight;
    }

    // Su desktop: comportamento normale con margini decenti
    const maxSafeHeight = windowSize.height - 80; // 40px margine per top/bottom
    return Math.min(requestedHeight, maxSafeHeight);
  };

  // Inizializza posizione e dimensioni all'apertura
  useEffect(() => {
    if (isOpen) {
      const isMobile = windowSize.width < 768;
      const isLandscape = windowSize.width > windowSize.height;

      // AUTO-FULLSCREEN su mobile (sia landscape che portrait)
      if (isMobile) {
        console.log('📱 Mobile detected - forcing fullscreen');

        // Margini Android-safe (landscape = 60px total, portrait = 40px total)
        const marginTotal = isLandscape ? 60 : 40; // Landscape: 30+30, Portrait: 20+20
        const marginLeft = isLandscape ? 30 : 20;   // Margine sinistro per centrare
        const marginTop = 10;  // Minimo margine superiore

        const maxWidth = windowSize.width - marginTotal;
        const maxHeight = windowSize.height - 20;  // Minimo per permettere scroll

        setSize({ width: maxWidth, height: maxHeight });
        setPosition({ x: marginLeft, y: marginTop });
        setIsMaximized(true);

        console.log('📱 Auto-fullscreen mobile:', {
          isLandscape,
          maxWidth,
          marginTotal,
          posX: marginLeft,
          posY: marginTop
        });
        return;
      }

      // Calcola dimensioni sicure per il dispositivo corrente
      const safeWidth = getSafeInitialWidth(initialWidth);
      const safeHeight = getSafeInitialHeight(initialHeight);

      console.log('📱 Safe modal sizing:', {
        requested: { width: initialWidth, height: initialHeight },
        safe: { width: safeWidth, height: safeHeight },
        viewport: { width: windowSize.width, height: windowSize.height },
        isMobile: windowSize.width < 768
      });

      // Imposta dimensioni sicure
      setSize({ width: safeWidth, height: safeHeight });

      // Calcola i rapporti proporzionali iniziali (solo se followWindowResize è attivo)
      if (followWindowResize) {
        const newWidthRatio = safeWidth / windowSize.width;
        const marginSpace = windowSize.width - safeWidth;
        const newMarginRatio = marginSpace / windowSize.width;

        setWidthRatio(newWidthRatio);
        setMarginRatio(newMarginRatio);

        console.log('🔧 Rapporti iniziali calcolati (safe sizing):', {
          widthRatio: newWidthRatio,
          marginRatio: newMarginRatio,
          windowWidth: windowSize.width,
          modalWidth: safeWidth,
          originalWidth: initialWidth
        });
      }

      // Imposta posizione
      if (initialX !== undefined && initialY !== undefined && !forceCenter) {
        // Assicurati che la posizione specificata non mandi la modale fuori schermo
        const safeX = Math.max(0, Math.min(initialX, windowSize.width - safeWidth));
        const safeY = Math.max(0, Math.min(initialY, windowSize.height - safeHeight));
        setPosition({ x: safeX, y: safeY });
      } else {
        // Centra la modal usando le dimensioni sicure
        const centerX = Math.max(0, (windowSize.width - safeWidth) / 2);
        const isMobile = windowSize.width < 768;
        const centerY = isMobile
          ? 10  // Su mobile: 10px dal top per UI browser sicura
          : Math.max(0, (windowSize.height - safeHeight) / 2);  // Desktop: centrato
        setPosition({ x: centerX, y: centerY });
      }

      setIsMaximized(false);
    }
  }, [isOpen, initialWidth, initialHeight, initialX, initialY, forceCenter, followWindowResize, windowSize.width, windowSize.height]);

  // Notifica cambio dimensioni
  useEffect(() => {
    if (onSizeChange && isOpen) {
      onSizeChange(size.width, size.height);
    }
  }, [size, onSizeChange, isOpen]);

  // Auto-scroll del tab attivo in vista quando cambia (SOLO su click, MAI dopo drag)
  useEffect(() => {
    // NON scrollare se è in corso un drag o appena finito
    if (!activeTab || !isOpen || isTabDragging || isMouseDownRef.current || wasDraggingRef.current) {
      return;
    }

    // Usa setTimeout per assicurarsi che il DOM sia aggiornato e il ref assegnato
    const timer = setTimeout(() => {
      const containerElement = tabsContainerRef.current;
      if (!containerElement) {
        console.log('🔍 Container non trovato');
        return;
      }

      // Trova il bottone del tab attivo direttamente dal DOM
      const tabElement = containerElement.querySelector(`[data-tab-id="${activeTab}"]`) as HTMLElement;
      if (!tabElement) {
        console.log('🔍 Tab element non trovato per:', activeTab);
        return;
      }

      // Calcola posizione REALE del tab usando getBoundingClientRect
      const containerRect = containerElement.getBoundingClientRect();
      const tabRect = tabElement.getBoundingClientRect();

      // Posizione del tab relativa al container + scroll corrente
      const tabLeftRelative = tabRect.left - containerRect.left;
      const tabLeftAbsolute = tabLeftRelative + containerElement.scrollLeft;
      const tabWidth = tabRect.width;
      const containerWidth = containerRect.width;

      // Calcola scroll necessario per centrare il tab
      const scrollLeft = tabLeftAbsolute - (containerWidth / 2) + (tabWidth / 2);

      console.log('✅ Auto-scroll al centro:', {
        activeTab,
        tabLeftRelative,
        tabLeftAbsolute,
        tabWidth,
        containerWidth,
        currentScroll: containerElement.scrollLeft,
        targetScroll: scrollLeft
      });

      // Smooth scroll al centro
      containerElement.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });
    }, 50); // Piccolo delay per assicurare DOM aggiornato

    return () => clearTimeout(timer);
  }, [activeTab, isOpen, isTabDragging, size.width]);

  // Gestione drag delle tabs - SOLO se mouse premuto + movimento
  const handleTabMouseDown = useCallback((e: React.MouseEvent) => {
    if (!tabsContainerRef.current) return;

    // Cancella qualsiasi animazione inerziale in corso
    if (inertiaAnimationRef.current) {
      cancelAnimationFrame(inertiaAnimationRef.current);
      inertiaAnimationRef.current = null;
    }

    // Marca che il mouse è premuto
    isMouseDownRef.current = true;
    wasDraggingRef.current = false;
    velocityRef.current = 0;

    const startX = e.clientX;
    const startTime = Date.now();
    const startScrollLeft = tabsContainerRef.current.scrollLeft;

    // Rimuovi focus da qualsiasi elemento per evitare scroll automatico
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    // IMPORTANTE: Previeni che qualsiasi bottone riceva focus
    const target = e.target as HTMLElement;
    const button = target.closest('button');
    if (button) {
      button.blur();
    }

    // Salva posizione iniziale
    setTabDragStart({
      x: startX,
      scrollLeft: startScrollLeft,
      time: startTime
    });

    lastPositionRef.current = { x: startX, time: startTime };

    // Previeni selezione testo e focus
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleTabMouseMove = useCallback((e: MouseEvent) => {
    // DRAG solo se mouse è premuto
    if (!isMouseDownRef.current || !tabsContainerRef.current) return;

    const currentTime = Date.now();
    const currentX = e.clientX;
    const deltaX = Math.abs(currentX - tabDragStart.x);

    // Se si muove di più di 3px con mouse premuto, attiva drag
    if (deltaX > 3) {
      if (!isTabDragging) {
        console.log('🎯 Drag attivato da movimento con mouse premuto');
        setIsTabDragging(true);
      }

      // Marca come effettivamente draggato
      wasDraggingRef.current = true;

      // Calcola velocità per inerzia (pixel/ms)
      const timeDelta = currentTime - lastPositionRef.current.time;
      if (timeDelta > 0) {
        const xDelta = currentX - lastPositionRef.current.x;
        velocityRef.current = xDelta / timeDelta;
      }
      lastPositionRef.current = { x: currentX, time: currentTime };

      // Esegui scroll
      const scrollDelta = currentX - tabDragStart.x;
      tabsContainerRef.current.scrollLeft = tabDragStart.scrollLeft - scrollDelta;
    }
  }, [tabDragStart.x, tabDragStart.scrollLeft, isTabDragging]);

  const handleTabMouseUp = useCallback((e: MouseEvent) => {
    const wasDragging = wasDraggingRef.current;
    const container = tabsContainerRef.current;

    isMouseDownRef.current = false;
    setIsTabDragging(false);

    if (wasDragging && container) {
      e.preventDefault();
      e.stopPropagation();

      console.log('🛑 Drag terminato - velocità:', velocityRef.current);

      // Blocca scroll automatico temporaneamente
      const originalOverflow = container.style.overflowX;
      container.style.overflowX = 'hidden';

      // Animazione inerziale con elasticità ai bordi (usa parametri physics)
      const startScroll = container.scrollLeft;
      const maxScroll = container.scrollWidth - container.clientWidth;
      let velocity = -velocityRef.current * 16; // converti px/ms in px/frame (60fps)
      let currentScroll = startScroll;

      const animate = () => {
        // Applica velocità
        currentScroll += velocity;

        // Elasticità ai bordi (usa physics.elasticity)
        if (currentScroll < 0) {
          currentScroll = 0;
          velocity = -velocity * physics.elasticity; // Rimbalzo
        } else if (currentScroll > maxScroll) {
          currentScroll = maxScroll;
          velocity = -velocity * physics.elasticity; // Rimbalzo
        }

        // Applica frizione (usa physics.friction)
        velocity *= physics.friction;

        // Aggiorna scroll
        container.scrollLeft = currentScroll;

        // Continua animazione se c'è ancora velocità (usa physics.minVelocity)
        if (Math.abs(velocity) > physics.minVelocity) {
          inertiaAnimationRef.current = requestAnimationFrame(animate);
        } else {
          // Animazione finita - ripristina overflow
          container.style.overflowX = originalOverflow;
          inertiaAnimationRef.current = null;
        }
      };

      // Avvia animazione solo se c'è velocità significativa
      if (Math.abs(velocity) > physics.minVelocity) {
        animate();
      } else {
        container.style.overflowX = originalOverflow;
      }

      // Ripristina overflow dopo 200ms come fallback
      setTimeout(() => {
        if (container.style.overflowX === 'hidden') {
          container.style.overflowX = originalOverflow;
        }
      }, 200);
    }

    setTimeout(() => {
      wasDraggingRef.current = false;
    }, 100);
  }, []);

  // Gestori globali per drag tabs - sempre attivi per catturare movimento
  useEffect(() => {
    document.addEventListener('mousemove', handleTabMouseMove);
    document.addEventListener('mouseup', handleTabMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleTabMouseMove);
      document.removeEventListener('mouseup', handleTabMouseUp);
    };
  }, [handleTabMouseMove, handleTabMouseUp]);

  // Gestione stili durante drag
  useEffect(() => {
    if (isTabDragging) {
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';

      if (tabsContainerRef.current) {
        const container = tabsContainerRef.current;
        const buttons = container.querySelectorAll('button');

        buttons.forEach(btn => {
          (btn as HTMLElement).style.pointerEvents = 'none';
        });

        return () => {
          document.body.style.cursor = '';
          document.body.style.userSelect = '';

          buttons.forEach(btn => {
            (btn as HTMLElement).style.pointerEvents = '';
          });
        };
      }

      return () => {
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [isTabDragging]);

  // Chiudi dropdown quando si clicca fuori
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.dropdown-container')) {
        setOpenDropdown(null);
      }
    };

    if (openDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [openDropdown]);

  // Forza re-render dei dropdown quando si scrolla per aggiornare posizione e clipping
  const [, forceUpdate] = useState({});
  useEffect(() => {
    const container = tabsContainerRef.current;
    if (!container || !openDropdown) return;

    const handleScroll = () => {
      // Solo aggiorna la posizione/clipping senza mai chiudere la dropdown
      // Il clipping CSS nasconderà automaticamente le parti fuori dalla modale
      forceUpdate({});
    };

    container.addEventListener('scroll', handleScroll);
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [openDropdown]);

  // Aggiorna visibilità frecce quando il container scrolla o cambia dimensione
  const updateArrows = useCallback(() => {
    const container = tabsContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setShowLeftArrow(scrollLeft > 5); // 5px di threshold
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 5);
  }, []);

  useEffect(() => {
    const container = tabsContainerRef.current;
    if (!container) return;

    updateArrows();

    const handleScroll = () => {
      updateArrows();
    };

    container.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', updateArrows);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateArrows);
    };
  }, [updateArrows, tabs]);

  // Gestione resize
  const handleMouseDown = (e: React.MouseEvent, direction: string) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizeDirection(direction);
  };

  // Gestione drag del titolo
  const handleTitleMouseDown = (e: React.MouseEvent) => {
    if (isMaximized) return;

    e.preventDefault();
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  // Mouse move globale
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const newX = Math.max(0, Math.min(windowSize.width - size.width, e.clientX - dragStart.x));
        const newY = Math.max(0, Math.min(windowSize.height - size.height, e.clientY - dragStart.y));
        setPosition({ x: newX, y: newY });
      }

      if (isResizing) {
        const rect = modalRef.current?.getBoundingClientRect();
        if (!rect) return;

        let newWidth = size.width;
        let newHeight = size.height;
        let newX = position.x;
        let newY = position.y;

        if (resizeDirection.includes('right')) {
          newWidth = Math.max(minWidth, Math.min(dynamicMaxWidth, e.clientX - rect.left));
        }
        if (resizeDirection.includes('left')) {
          const deltaX = rect.left - e.clientX;
          newWidth = Math.max(minWidth, Math.min(dynamicMaxWidth, size.width + deltaX));
          newX = Math.max(0, e.clientX);
        }
        if (resizeDirection.includes('bottom')) {
          newHeight = Math.max(minHeight, Math.min(dynamicMaxHeight, e.clientY - rect.top));
        }
        if (resizeDirection.includes('top')) {
          const deltaY = rect.top - e.clientY;
          newHeight = Math.max(minHeight, Math.min(dynamicMaxHeight, size.height + deltaY));
          newY = Math.max(0, e.clientY);
        }

        setSize({ width: newWidth, height: newHeight });
        setPosition({ x: newX, y: newY });

        // Aggiorna i rapporti proporzionali durante il ridimensionamento manuale
        if (followWindowResize && windowSize.width > 0) {
          const newWidthRatio = newWidth / windowSize.width;
          const marginSpace = windowSize.width - newWidth;
          const newMarginRatio = Math.max(0, marginSpace / windowSize.width);

          setWidthRatio(newWidthRatio);
          setMarginRatio(newMarginRatio);

          console.log('🔧 Rapporti aggiornati durante resize:', {
            newWidth,
            windowWidth: windowSize.width,
            widthRatio: newWidthRatio,
            marginRatio: newMarginRatio
          });
        }
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
      setResizeDirection('');
    };

    // Handler per quando il mouse esce dalla finestra del browser
    const handleMouseLeave = (e: MouseEvent) => {
      // Se il mouse esce dalla viewport, termina il drag
      if (e.clientY <= 0 || e.clientX <= 0 ||
          e.clientX >= window.innerWidth || e.clientY >= window.innerHeight) {
        handleMouseUp();
      }
    };

    // Handler per quando la finestra perde il focus (alt-tab, click su altra app)
    const handleWindowBlur = () => {
      handleMouseUp();
    };

    // Handler per evitare che il context menu blocchi il drag
    const handleContextMenu = (e: MouseEvent) => {
      if (isDragging || isResizing) {
        e.preventDefault();
        handleMouseUp();
      }
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('mouseleave', handleMouseLeave);
      document.addEventListener('contextmenu', handleContextMenu);
      window.addEventListener('blur', handleWindowBlur);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = isResizing ? getResizeCursor(resizeDirection) : 'move';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('contextmenu', handleContextMenu);
      window.removeEventListener('blur', handleWindowBlur);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isDragging, isResizing, dragStart, resizeDirection, size, position, windowSize.width, windowSize.height, minWidth, minHeight, dynamicMaxWidth, dynamicMaxHeight]);

  // Gestione resize della finestra del browser
  useEffect(() => {
    const handleWindowResize = () => {
      const newWindowSize = {
        width: window.innerWidth,
        height: window.innerHeight
      };
      // const prevWindowSize = windowSize; // Non più utilizzato per il resize proporzionale
      setWindowSize(newWindowSize);

      const isMobileResize = newWindowSize.width < 768;
      const newMaxWidth = maxWidth || newWindowSize.width - 40;
      const newMaxHeight = maxHeight || (isMobileResize ? newWindowSize.height - 50 : newWindowSize.height - 80);

      // Rileva landscape con altezza ridotta
      const isLowHeightLandscape = newWindowSize.height < 600 && newWindowSize.width > newWindowSize.height;

      // Calcola margini per landscape (controlli Android)
      const landscapeWidth = isLowHeightLandscape ? newWindowSize.width - 120 : newMaxWidth;
      const landscapeHeight = isLowHeightLandscape ? newWindowSize.height - 20 : newMaxHeight;
      const landscapePosX = isLowHeightLandscape ? 10 : 20;
      const landscapePosY = isLowHeightLandscape ? 10 : isMobileResize ? 10 : 20;

      console.log('🔄 Window resize:', {
        newSize: newWindowSize,
        isLowHeightLandscape,
        isMaximized,
        landscapeWidth,
        currentWidth: size.width
      });

      // Se la modale è massimizzata, aggiorna le sue dimensioni
      if (isMaximized) {
        // Se NON è più landscape (width < height) e la modale era stata auto-maximized, ripristina
        // IMPORTANTE: usa width/height invece di isLowHeightLandscape per evitare falsi positivi
        // quando il browser cambia l'altezza reportata (es. nasconde barra indirizzi)
        const isStillLandscape = newWindowSize.width > newWindowSize.height;

        if (!isStillLandscape && savedSize && savedSize.width > 0) {
          console.log('📱 Exiting landscape - restoring normal size');
          setSize(savedSize);
          setPosition(savedPosition);
          setIsMaximized(false);
          return;
        }

        // Se ancora in landscape, usa margini landscape anche se height >= 600
        const finalWidth = isStillLandscape && newWindowSize.height < 650
          ? newWindowSize.width - 150  // Landscape: margini Android
          : landscapeWidth;
        const finalHeight = isStillLandscape && newWindowSize.height < 650
          ? newWindowSize.height - 20
          : landscapeHeight;
        const finalPosX = isStillLandscape && newWindowSize.height < 650 ? 10 : landscapePosX;

        console.log('📱 Updating maximized modal size:', {
          finalWidth,
          finalHeight,
          isStillLandscape,
          height: newWindowSize.height
        });
        setSize({ width: finalWidth, height: finalHeight });
        setPosition({ x: finalPosX, y: landscapePosY });
        return; // Exit early per le modali massimizzate
      }

      // Se entra in landscape ridotto, forza fullscreen
      if (isLowHeightLandscape) {
        console.log('📱 Entering low height landscape - forcing fullscreen');
        setSavedSize(size);
        setSavedPosition(position);
        setSize({ width: landscapeWidth, height: landscapeHeight });
        setPosition({ x: landscapePosX, y: landscapePosY });
        setIsMaximized(true);
        return;
      }

      // Calcola il rapporto di ridimensionamento della finestra (non più utilizzato per il resize proporzionale)
      // const windowWidthRatio = prevWindowSize.width > 0 ? newWindowSize.width / prevWindowSize.width : 1;
      // const windowHeightRatio = prevWindowSize.height > 0 ? newWindowSize.height / prevWindowSize.height : 1;

      // Se followWindowResize è disattivo o rapporti proporzionali non disponibili, fallback
      if (!followWindowResize || widthRatio === null || marginRatio === null) {
        // Fallback: solo constrains dei limiti massimi
        setSize(prevSize => {
          const newSize = {
            width: Math.min(prevSize.width, newMaxWidth),
            height: Math.min(prevSize.height, newMaxHeight)
          };

          setPosition(prevPosition => {
            const maxX = Math.max(0, newMaxWidth - newSize.width);
            const maxY = Math.max(0, newMaxHeight - newSize.height);
            return {
              x: Math.min(Math.max(0, prevPosition.x), maxX),
              y: Math.min(Math.max(0, prevPosition.y), maxY)
            };
          });

          return newSize;
        });
        return;
      }

      // Auto-resize proporzionale: larghezza e altezza adattive
      setSize(prevSize => {
        const isMobile = newWindowSize.width < 768;

        // Calcola la nuova larghezza proporzionale
        const proposedWidth = newWindowSize.width * widthRatio;
        const newWidth = Math.max(minWidth, Math.min(newMaxWidth, proposedWidth));

        // Su mobile: usa massimo spazio disponibile (come getSafeInitialHeight)
        // Su desktop: mantieni altezza precedente
        const newHeight = isMobile
          ? Math.min(newMaxHeight, newWindowSize.height - 50)  // Mobile: massimo disponibile con margini sicuri
          : Math.min(newMaxHeight, prevSize.height);           // Desktop: mantieni altezza

        // Calcola margini proporzionali
        const totalMarginSpace = newWindowSize.width - newWidth;
        const sideMargin = Math.max(2, totalMarginSpace / 2); // Min 2px per lato

        // Posizione centrata con margini proporzionali
        setPosition(prevPosition => {
          const newX = sideMargin;
          // Su mobile: sempre 10px dal top, su desktop mantieni posizione o centra
          const newY = isMobile
            ? 10
            : Math.max(0, Math.min(newMaxHeight - newHeight, prevPosition.y));

          return { x: newX, y: newY };
        });

        console.log('🔧 Auto-resize proporzionale:', {
          windowWidth: newWindowSize.width,
          isMobile,
          widthRatio,
          proposedWidth,
          newWidth,
          sideMargin,
          newHeight,
          maxAvailable: newWindowSize.height - 50
        });

        return { width: newWidth, height: newHeight };
      });
    };

    if (isOpen) {
      window.addEventListener('resize', handleWindowResize);
      return () => {
        window.removeEventListener('resize', handleWindowResize);
      };
    }
  }, [isOpen, isMaximized, maxWidth, maxHeight, followWindowResize, widthRatio, marginRatio, minWidth, minHeight]);

  const getResizeCursor = (direction: string) => {
    if (direction.includes('top') && direction.includes('left')) return 'nw-resize';
    if (direction.includes('top') && direction.includes('right')) return 'ne-resize';
    if (direction.includes('bottom') && direction.includes('left')) return 'sw-resize';
    if (direction.includes('bottom') && direction.includes('right')) return 'se-resize';
    if (direction.includes('top') || direction.includes('bottom')) return 'ns-resize';
    if (direction.includes('left') || direction.includes('right')) return 'ew-resize';
    return 'default';
  };

  const handleMaximize = () => {
    const isMobile = windowSize.width < 768;
    const isLowHeight = windowSize.height < 600;
    const isLandscape = windowSize.width > windowSize.height;
    const isLowHeightLandscape = isLowHeight && isLandscape;

    // Se già maximized E NON in landscape, de-maximizza
    // Se già maximized E in landscape, ri-applica dimensioni (caso comune: rotazione o click ripetuto)
    if (isMaximized && !isLowHeightLandscape) {
      console.log('🔽 De-maximize (restore normal size)');
      setSize(savedSize);
      setPosition(savedPosition);
      setIsMaximized(false);
      return;
    }

    // Maximize o ri-maximize in landscape
    console.log('🔼 Maximize:', { isLowHeightLandscape, isMaximized });

    // In landscape (altezza ridotta), usa STESSI margini dell'auto-fullscreen
    const landscapeMarginTotal = 60; // 30px per lato (ALLINEATO con auto-fullscreen riga 230)
    const landscapeMarginLeft = 30;   // Margine sinistro per centrare

    const maxWidth = isLowHeightLandscape
      ? windowSize.width - landscapeMarginTotal  // 60px totali per controlli Android
      : windowSize.width - 40;

    // In modalità altezza ridotta, usa dimensioni che permettono lo scroll della pagina
    const maxHeight = isLowHeight
      ? windowSize.height - 20  // Minimo margine per permettere scroll
      : isMobile
        ? windowSize.height - 50
        : windowSize.height - 80;

    const posY = isLowHeight ? 10 : isMobile ? 10 : 20;
    const posX = isLowHeightLandscape ? landscapeMarginLeft : 20;  // Centrato in landscape

    console.log('🔘 Manual fullscreen:', {
      isLowHeight,
      isLandscape,
      isLowHeightLandscape,
      maxWidth,
      maxHeight,
      landscapeMarginTotal,
      posX,
      posY
    });

    // Salva solo se non era già maximized
    if (!isMaximized) {
      setSavedSize(size);
      setSavedPosition(position);
    }

    setSize({ width: maxWidth, height: maxHeight });
    setPosition({ x: posX, y: posY });
    setIsMaximized(true);
  };

  const handleReset = () => {
    const isMobile = windowSize.width < 768;

    // Calcola dimensioni ottimali: 90% dello spazio disponibile
    const optimalWidth = Math.floor(windowSize.width * 0.9);
    const optimalHeight = Math.floor(windowSize.height * 0.9);

    // Usa le dimensioni ottimali o le iniziali, quale è più piccola
    const resetWidth = Math.min(optimalWidth, Math.max(initialWidth, minWidth));
    const resetHeight = Math.min(optimalHeight, Math.max(initialHeight, minHeight));

    // Centra la modale
    const centerX = Math.max(0, (windowSize.width - resetWidth) / 2);
    const centerY = isMobile
      ? 10  // Mobile: 10px dal top
      : Math.max(0, (windowSize.height - resetHeight) / 2);  // Desktop: centrato

    setPosition({ x: centerX, y: centerY });
    setSize({ width: resetWidth, height: resetHeight });
    setIsMaximized(false);
  };

  // Listener per scroll del container esterno (modalità altezza ridotta)
  useEffect(() => {
    if (!isOpen || windowSize.height >= 600) return;

    const handleScroll = () => {
      // Forza re-render dei dropdown per aggiornare posizioni
      setScrollTick(tick => tick + 1);
    };

    // Trova il container esterno con scroll
    const scrollContainer = document.querySelector('.fixed.inset-0.z-50');
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, [isOpen, windowSize.height]);

  if (!isOpen) return null;

  // CSS personalizzato per animazioni (usato in entrambe le modalità)
  const customStyles = (
    <style>{`
      @keyframes shine {
        0% {
          transform: translateX(-150%) skewX(-12deg);
          opacity: 0;
        }
        50% {
          opacity: 1;
        }
        100% {
          transform: translateX(250%) skewX(-12deg);
          opacity: 0;
        }
      }
      .animate-shine {
        animation: shine 2.5s ease-in-out infinite;
      }
      .hover\\:scale-102:hover {
        transform: scale(1.02);
      }
      .bg-gradient-radial {
        background: radial-gradient(circle at center, var(--tw-gradient-stops));
      }
      .border-b-3 {
        border-bottom-width: 3px;
      }
    `}</style>
  );

  // Contenuto della modale (riusato in entrambe le modalità)
  const modalContent = (
      <div
        ref={modalRef}
        className={`absolute bg-slate-800 border border-slate-600 shadow-2xl rounded-lg flex flex-col
                   ${windowSize.height < 600 ? 'overflow-visible' : 'overflow-hidden'}
                   animate-in zoom-in-95 slide-in-from-bottom-4 fade-in duration-500 ease-out
                   transition-all motion-reduce:transition-none motion-reduce:animate-none
                   hover:shadow-blue-900/20 hover:shadow-2xl hover:border-slate-500
                   focus-within:ring-2 focus-within:ring-blue-500/30 focus-within:border-blue-500/50 ${className}`}
        style={{
          left: position.x,
          top: position.y,
          width: size.width,
          height: windowSize.height < 600 ? 'auto' : size.height,
          minHeight: windowSize.height < 600 ? windowSize.height - 20 : undefined,
          zIndex: 51,
          pointerEvents: 'auto', // Sempre cliccabile anche quando container ha pointer-events: none
          transition: isResizing || isDragging ? 'none' : 'width 0.3s ease-out, height 0.3s ease-out, left 0.3s ease-out, top 0.3s ease-out'
        }}
        onMouseDown={onModalFocus}
      >
        {/* Header with staggered animations - Fixed height (nascondibile con hideHeader) */}
        {!hideHeader && (
          <div
            className="bg-slate-700 border-b border-slate-600 px-4 py-3 flex items-center justify-between cursor-move select-none flex-shrink-0"
            onMouseDown={enableDrag ? handleTitleMouseDown : undefined}
          >
            <div className="min-w-0 flex-shrink-0 mr-4 animate-in slide-in-from-left duration-400 delay-100">
              <div className="flex items-center gap-3">
                {titleIcon && (
                  <img src={titleIcon} alt="" className="w-10 h-10 object-contain" />
                )}
                <div>
                  <h3 className={`text-white ${getTitleResponsiveClass()}`}>{title}</h3>
                  {subtitle && <p className={`text-slate-300 ${getSubtitleResponsiveClass()}`}>{subtitle}</p>}
                </div>
              </div>
            </div>
            {/* Centro dell'header (opzionale) */}
            {headerCenter && (
              <div className="flex-1 flex justify-center animate-in fade-in duration-400 delay-150">
                {headerCenter}
              </div>
            )}
            {/* Azioni custom a destra (prima dei controlli) */}
            {headerActions && (
              <div className="flex items-center mr-3 animate-in fade-in duration-400 delay-175 flex-shrink-0">
                {headerActions}
              </div>
            )}
            <div className="flex items-center space-x-2 animate-in slide-in-from-right duration-400 delay-200 flex-shrink-0">
              <button
                onClick={handleReset}
                className="text-slate-400 hover:text-white p-1 rounded transition-all duration-200 hover:scale-110 hover:rotate-180 hover:bg-slate-600"
                title="Reset dimensioni"
              >
                <RotateCcw size={16} />
              </button>
              <button
                onClick={handleMaximize}
                className="text-slate-400 hover:text-white p-1 rounded transition-all duration-200 hover:scale-110 hover:bg-slate-600"
                title={isMaximized ? "Ripristina" : "Massimizza"}
              >
                {isMaximized ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
              </button>
              <button
                onClick={onClose}
                className="text-slate-400 hover:text-red-400 p-1 rounded transition-all duration-200 hover:scale-110 hover:bg-red-900/30 hover:rotate-90"
                title="Chiudi"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Wrapper per tutto il contenuto sotto header - usa flex-1 per riempire spazio rimanente */}
        <div className="flex-1 flex flex-col min-h-0 relative group">
          {/* Tabs - Opzionali con scroll orizzontale */}
          {tabs && tabs.length > 0 && (
            <div className="relative border-b border-slate-600 bg-slate-700 flex-shrink-0 overflow-x-hidden overflow-y-visible">
              {/* Freccia sinistra - appare quando c'è contenuto scrollato a sinistra */}
              {showLeftArrow && (
                <button
                  onClick={() => {
                    tabsContainerRef.current?.scrollBy({ left: -200, behavior: 'smooth' });
                  }}
                  className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-slate-700 via-slate-700/95 to-transparent z-20 flex items-center justify-center hover:from-blue-600/80 transition-all duration-200 group/arrow"
                >
                  <svg className="w-5 h-5 text-blue-400 drop-shadow-lg group-hover/arrow:text-white group-hover/arrow:scale-125 transition-all duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}

              {/* Freccia destra - appare quando c'è contenuto scrollato a destra */}
              {showRightArrow && (
                <button
                  onClick={() => {
                    tabsContainerRef.current?.scrollBy({ left: 200, behavior: 'smooth' });
                  }}
                  className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-slate-700 via-slate-700/95 to-transparent z-20 flex items-center justify-center hover:from-blue-600/80 transition-all duration-200 group/arrow"
                >
                  <svg className="w-5 h-5 text-blue-400 drop-shadow-lg group-hover/arrow:text-white group-hover/arrow:scale-125 transition-all duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}

              {/* Draggable tabs container con dropdown */}
              <div
                ref={tabsContainerRef}
                onMouseDown={handleTabMouseDown}
                className={`flex gap-1 px-2 py-1 overflow-x-auto scrollbar-thin ${
                  isTabDragging ? 'cursor-grabbing select-none' : 'cursor-grab'
                }`}
                style={{
                  scrollBehavior: isTabDragging ? 'auto' : 'smooth',
                  scrollPaddingInline: 0,
                  overflowAnchor: 'none',
                  contain: 'layout'
                }}
              >
                {tabs.map(tab => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  const hasChildren = tab.children && tab.children.length > 0;
                  const isDropdownOpen = openDropdown === tab.id;

                  return (
                    <div key={tab.id} className="relative flex-shrink-0 dropdown-container">
                      <button
                        ref={isActive ? activeTabRef : null}
                        data-tab-id={tab.id}
                        tabIndex={-1}
                        onMouseDown={(e) => {
                          // Se inizia drag, previeni focus del bottone
                          if (isTabDragging) {
                            e.preventDefault();
                          }
                        }}
                        onClick={(e) => {
                          e.stopPropagation();

                          // Blocca il click se è stato un drag (usa ref per valore sincronizzato)
                          if (wasDraggingRef.current) {
                            console.log('🚫 Click bloccato - era un drag');
                            e.preventDefault();
                            return;
                          }

                          if (hasChildren) {
                            // Menu con dropdown: toggle dropdown E cambia pagina principale
                            if (isDropdownOpen) {
                              // Seconda volta: chiudi dropdown
                              console.log('🔽 Chiusura dropdown');
                              setOpenDropdown(null);
                            } else {
                              // Prima volta: apri dropdown + cambia pagina principale + scrolla al centro
                              console.log('🔼 Apertura dropdown + cambio pagina principale');
                              setOpenDropdown(tab.id);
                              onTabChange && onTabChange(tab.id); // Cambia alla pagina principale
                            }
                          } else {
                            // Menu senza dropdown: chiudi eventuali dropdown e cambia tab
                            setOpenDropdown(null);
                            onTabChange && onTabChange(tab.id);
                          }
                        }}
                        style={{
                          scrollMargin: 0,
                          scrollSnapMargin: 0
                        }}
                        className={`
                          relative flex items-center gap-2 transition-all duration-300 min-w-0 overflow-hidden
                          ${size.width < 600 ? 'px-3 py-2 text-xs' : 'px-4 py-2.5 text-sm'}
                          ${isActive
                            ? `${effects.enableGradients ? `bg-gradient-to-br from-slate-600 via-slate-550 to-slate-700` : 'bg-slate-600'}
                               text-white border-b-3 border-${effects.accentColor}-400
                               ${effects.enableShadows ? `shadow-2xl shadow-${effects.glowColor}-500/50 drop-shadow-lg` : 'shadow-lg'}
                               ${effects.enableGlow ? `ring-2 ring-${effects.glowColor}-400/70 ring-offset-1 ring-offset-slate-800` : ''}
                               scale-110 font-semibold`
                            : `text-slate-300 hover:text-white
                               ${effects.enableGradients ? 'hover:bg-gradient-to-br hover:from-slate-600 hover:via-slate-550 hover:to-slate-700' : 'hover:bg-slate-600'}
                               ${effects.enableShadows ? 'hover:shadow-xl hover:shadow-slate-900/50' : 'hover:shadow-md'}
                               ${effects.enablePulse ? 'hover:animate-pulse' : ''}
                               hover:scale-105 hover:ring-1 hover:ring-slate-500/30`
                          }
                          ${isDropdownOpen ? 'bg-slate-600 ring-1 ring-blue-500/40' : ''}
                        `}
                      >
                        {/* Glow pulsante di sfondo per tab attivi */}
                        {effects.enableGlow && isActive && (
                          <div className="absolute inset-0 bg-gradient-radial from-blue-500/20 via-transparent to-transparent animate-pulse" />
                        )}

                        {/* Effetto brillio shine - animato e più visibile */}
                        {effects.enableShine && isActive && (
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 animate-shine pointer-events-none" />
                        )}

                        {Icon && <Icon size={size.width < 600 ? 14 : 16} className="flex-shrink-0 relative z-10 drop-shadow-md" />}
                        <span className={`${size.width < 400 ? 'hidden' : 'whitespace-nowrap'} relative z-10`}>
                          {size.width < 600 && tab.shortLabel ? tab.shortLabel : tab.label}
                        </span>
                        {hasChildren && (
                          <svg
                            className={`w-3 h-3 transition-transform duration-200 relative z-10 ${isDropdownOpen ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Dropdown renderizzati FUORI dal container di scroll con animazioni professionali */}
              {tabs.map(tab => {
                const hasChildren = tab.children && tab.children.length > 0;
                const isDropdownOpen = openDropdown === tab.id;

                if (!hasChildren || !isDropdownOpen) return null;

                // Trova il bottone per posizionare il dropdown
                const button = tabsContainerRef.current?.querySelector(`[data-tab-id="${tab.id}"]`) as HTMLElement;
                if (!button) return null;

                const buttonRect = button.getBoundingClientRect();
                const modalRect = modalRef.current?.getBoundingClientRect();
                if (!modalRect) return null;

                // Calcola posizionamento dropdown - SEMPRE sotto il bottone
                const dropdownWidth = 200;
                const isMobileWidth = windowSize.width < 768;

                // SEMPRE fixed positioning: getBoundingClientRect già tiene conto dello scroll
                let dropdownLeft = buttonRect.left;
                const dropdownTop = buttonRect.bottom + 4;

                // Su desktop: calcola se c'è più spazio a destra o sinistra per posizionamento orizzontale
                if (!isMobileWidth) {
                  const spaceOnRight = modalRect.right - buttonRect.left;
                  const spaceOnLeft = buttonRect.right - modalRect.left;

                  // Se dropdown non entra a destra, prova a sinistra
                  if (spaceOnRight < dropdownWidth && spaceOnLeft > dropdownWidth) {
                    dropdownLeft = buttonRect.right - dropdownWidth;
                  }
                }

                // NON forzare il dropdown dentro i bordi - lascia che segua il bottone
                // Il clip-path si occuperà di nasconderlo quando esce

                // Calcola clipping per nascondere dropdown quando scrolla fuori dai bordi della modale
                const clipTop = Math.max(0, modalRect.top - dropdownTop);
                const clipRight = Math.max(0, dropdownLeft + dropdownWidth - modalRect.right);
                const clipBottom = Math.max(0, dropdownTop + 300 - modalRect.bottom); // 300px altezza massima dropdown
                const clipLeft = Math.max(0, modalRect.left - dropdownLeft);

                return (
                  <div
                    key={`dropdown-${tab.id}`}
                    className="fixed min-w-[200px] bg-gradient-to-b from-slate-700 via-slate-750 to-slate-800 border-2 border-blue-500/40 rounded-lg shadow-2xl shadow-blue-500/40 backdrop-blur-md animate-in slide-in-from-top-4 fade-in duration-300 ring-1 ring-blue-400/30"
                    style={{
                      top: dropdownTop,
                      left: dropdownLeft,
                      zIndex: 99999,
                      clipPath: `inset(${clipTop}px ${clipRight}px ${clipBottom}px ${clipLeft}px)`
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* Glow effect superiore più intenso */}
                    <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-blue-400 to-transparent shadow-lg shadow-blue-500/50" />

                    {/* Glow effect laterale */}
                    <div className="absolute inset-0 bg-gradient-to-b from-blue-500/5 via-transparent to-transparent rounded-lg pointer-events-none" />

                    {tab.children!.map((child, index) => {
                      const ChildIcon = child.icon;
                      const isChildActive = activeTab === child.id;

                      return (
                        <button
                          key={child.id}
                          onClick={() => {
                            onTabChange && onTabChange(child.id);
                            setOpenDropdown(null);
                          }}
                          className={`
                            relative group w-full flex items-center gap-3 px-4 py-3 text-sm transition-all duration-300 overflow-hidden
                            hover:bg-gradient-to-r hover:from-slate-600 hover:via-slate-550 hover:to-slate-650 hover:scale-[1.03] hover:shadow-xl hover:shadow-blue-500/30 hover:pl-5
                            ${isChildActive
                              ? 'bg-gradient-to-r from-blue-600/30 via-blue-700/20 to-slate-600 text-blue-200 border-l-[5px] border-blue-400 shadow-lg shadow-blue-500/40 font-semibold'
                              : 'text-slate-300 hover:text-white hover:border-l-2 hover:border-blue-500/50'
                            }
                            ${index === 0 ? 'rounded-t-lg' : ''}
                            ${index === tab.children!.length - 1 ? 'rounded-b-lg' : 'border-b border-slate-600/50'}
                          `}
                        >
                          {/* Glow hover effect */}
                          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/0 via-blue-500/10 to-blue-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

                          {ChildIcon && (
                            <ChildIcon
                              size={18}
                              className={`flex-shrink-0 transition-all duration-300 relative z-10 ${
                                isChildActive
                                  ? 'text-blue-300 drop-shadow-[0_0_8px_rgba(59,130,246,0.8)] scale-110'
                                  : 'text-slate-400 group-hover:text-blue-400 group-hover:scale-125 group-hover:drop-shadow-[0_0_6px_rgba(59,130,246,0.6)]'
                              }`}
                            />
                          )}
                          <span className="text-left whitespace-nowrap transition-all duration-300 group-hover:translate-x-2 relative z-10">
                            {child.label}
                          </span>
                          {isChildActive && (
                            <div className="ml-auto relative z-10">
                              <div className="w-2.5 h-2.5 rounded-full bg-blue-400 animate-pulse shadow-lg shadow-blue-500/70 ring-2 ring-blue-300/50" />
                            </div>
                          )}
                        </button>
                      );
                    })}

                    {/* Glow effect inferiore */}
                    <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/30 to-transparent" />
                  </div>
                );
              })}
            </div>
        )}

        {/* Content - Scroll sempre interno alla modale */}
        <div className="flex flex-col flex-1 min-h-0">
          <div className={`flex-1 p-0 min-h-0 relative ${allowContentOverflow ? 'overflow-visible' : 'overflow-x-hidden overflow-y-auto'}`}>
            {children}
            {/* Overlay per bloccare eventi mouse dell'iframe durante resize/drag */}
            {(isResizing || isDragging) && (
              <div
                className="absolute inset-0 z-50"
                style={{ cursor: isResizing ? getResizeCursor(resizeDirection) : 'move' }}
              />
            )}
          </div>

          {/* Footer - Gestito internamente con responsive design */}
          {(footer || onSave) && (
            <div className={`bg-slate-700 border-t border-slate-600 px-3 flex items-center justify-center relative z-10 ${
              windowSize.height < 600
                ? '' // Altezza ridotta: nessun vincolo flex-shrink, scorre con la pagina
                : 'flex-shrink-0' // Normale: footer bloccato
            } ${
              size.width < 600
                ? 'py-1 h-[50px]'  // Mobile: altezza fissa 50px con padding minimo
                : 'py-5 h-[90px]'  // Desktop: altezza fissa 90px
            }`}>
              {/* DEBUG - Only in development mode */}
              {process.env.NODE_ENV === 'development' && (
                <div className="absolute -top-8 left-4 bg-green-500 text-white text-xs px-2 py-1 z-50">
                  FOOTER: W={size.width}px | Mobile={size.width < 600 ? 'YES' : 'NO'}
                </div>
              )}

              {footer ? (
                footer
              ) : onSave ? (
                <div className="flex w-full justify-center animate-in slide-in-from-bottom duration-400 delay-300">
                  {/* Pulsanti centrati con micro-animations professionali */}
                  <div className={`flex ${size.width < 600 ? 'w-full justify-between gap-2' : 'space-x-3'}`}>
                    <button
                      onClick={onClose}
                      className={`${
                        size.width < 600
                          ? 'flex-1 py-1.5 px-2 text-xs font-medium'  // Mobile: py-1.5 (+10% altezza)
                          : 'px-5 py-2.5 text-sm font-medium'
                      } text-slate-300 hover:text-white transition-all duration-200 border border-slate-500 rounded
                         hover:scale-105 hover:border-slate-400 hover:shadow-lg hover:shadow-slate-500/20
                         active:scale-95 active:duration-75 focus:ring-2 focus:ring-slate-400 focus:ring-opacity-50`}
                    >
                      Annulla
                    </button>
                    <button
                      onClick={onSave}
                      className={`bg-blue-600 hover:bg-blue-700 text-white ${
                        size.width < 600
                          ? 'flex-1 py-1.5 px-2 text-xs font-medium'  // Mobile: py-1.5 (+10% altezza)
                          : 'px-6 py-2.5 text-sm font-medium'
                      } rounded flex items-center justify-center transition-all duration-200
                         hover:scale-105 hover:bg-blue-500 hover:shadow-lg hover:shadow-blue-500/30
                         active:scale-95 active:duration-75 focus:ring-2 focus:ring-blue-400 focus:ring-opacity-50
                         group relative overflow-hidden`}
                      style={{ gap: size.width < 600 ? '0.25rem' : '0.5rem' }}
                    >
                      {/* Shimmer effect on hover */}
                      <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
                      <Save size={size.width < 600 ? 16 : 16} className="transition-transform duration-200 group-hover:rotate-12 flex-shrink-0" />
                      <span className="truncate">{context ? 'Aggiorna' : 'Crea'} Contesto</span>
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </div>
        </div>
        {/* Fine wrapper contenuto */}

        {/* Resize handles - z-index alto per essere sopra iframe */}
        {!isMaximized && enableResize && (
          <>
            {/* Edges - 14px di area cliccabile (aumentata per facilità d'uso) */}
            <div
              className="absolute top-0 left-5 right-5 h-[14px] cursor-ns-resize z-[60]"
              style={{ transform: 'translateY(-50%)' }}
              onMouseDown={(e) => handleMouseDown(e, 'top')}
            />
            <div
              className="absolute bottom-0 left-5 right-5 h-[14px] cursor-ns-resize z-[60]"
              style={{ transform: 'translateY(50%)' }}
              onMouseDown={(e) => handleMouseDown(e, 'bottom')}
            />
            <div
              className="absolute left-0 top-5 bottom-5 w-[14px] cursor-ew-resize z-[60]"
              style={{ transform: 'translateX(-50%)' }}
              onMouseDown={(e) => handleMouseDown(e, 'left')}
            />
            <div
              className="absolute right-0 top-5 bottom-5 w-[14px] cursor-ew-resize z-[60]"
              style={{ transform: 'translateX(50%)' }}
              onMouseDown={(e) => handleMouseDown(e, 'right')}
            />

            {/* Corners - 20px di area cliccabile (aumentata per facilità d'uso) */}
            <div
              className="absolute top-0 left-0 w-[20px] h-[20px] cursor-nw-resize z-[61]"
              style={{ transform: 'translate(-50%, -50%)' }}
              onMouseDown={(e) => handleMouseDown(e, 'top-left')}
            />
            <div
              className="absolute top-0 right-0 w-[20px] h-[20px] cursor-ne-resize z-[61]"
              style={{ transform: 'translate(50%, -50%)' }}
              onMouseDown={(e) => handleMouseDown(e, 'top-right')}
            />
            <div
              className="absolute bottom-0 left-0 w-[20px] h-[20px] cursor-sw-resize z-[61]"
              style={{ transform: 'translate(-50%, 50%)' }}
              onMouseDown={(e) => handleMouseDown(e, 'bottom-left')}
            />
            <div
              className="absolute bottom-0 right-0 w-[20px] h-[20px] cursor-se-resize z-[61]"
              style={{ transform: 'translate(50%, 50%)' }}
              onMouseDown={(e) => handleMouseDown(e, 'bottom-right')}
            />
          </>
        )}
      </div>
  );

  // Se useAbsolutePositioning è true, renderizza solo la modale senza wrapper
  // Questo permette allo scroll del parent di funzionare correttamente
  if (useAbsolutePositioning) {
    return (
      <>
        {customStyles}
        {modalContent}
      </>
    );
  }

  // Modalità normale: modale con wrapper e backdrop
  return (
    <>
      {customStyles}
      <div
        className="fixed inset-0 overflow-hidden"
        style={{
          zIndex,
          pointerEvents: disableBackdropClick ? 'none' : 'auto'
        }}
      >
        {/* Backdrop with smooth fade-in - z-10 to ensure it's below modal (z-51) */}
        <div
          className="absolute inset-0 z-10 animate-in fade-in duration-300"
          onClick={disableBackdropClick ? undefined : onClose}
          style={{
            pointerEvents: disableBackdropClick ? 'none' : 'auto',
            backgroundColor: disableBackdropDarkening ? 'transparent' : `rgba(0, 0, 0, ${backdropDarkeningAmount / 100})`,
            backdropFilter: disableBackdropBlur ? 'none' : `blur(${backdropBlurAmount}px)`,
            WebkitBackdropFilter: disableBackdropBlur ? 'none' : `blur(${backdropBlurAmount}px)`
          }}
        />
        {modalContent}
      </div>
    </>
  );
};

export default ResizableModal;